/**
 * Analyzer Bridge — integrates artifact-contracts-analyzer as a standard
 * runtime tool provider.
 *
 * Wraps analyzeImpact, generateContextPack, and buildCallGraph behind a
 * lazily-initialised DependencyGraph, and exposes them as SdkFunctionTool[]
 * for adapter consumption.
 *
 * `artifact-contracts-analyzer` is a peerDependency (optional) loaded via
 * dynamic import; when absent, every method returns a descriptive error
 * rather than crashing.
 */

import type { SdkFunctionTool } from "./library-tool-sdk.js";

// ---------------------------------------------------------------------------
// Re-exported duck types — the bridge never statically imports the analyzer
// package, so we define the minimal shapes callers need.
// ---------------------------------------------------------------------------

/** Subset of DependencyGraph that the bridge caches and exposes. */
export type DependencyGraph = Record<string, unknown>;

/** Subset of ImpactReport returned by analyzeImpact. */
export type ImpactReport = Record<string, unknown>;

/** Subset of ContextPack returned by generateContextPack. */
export type ContextPack = {
  entries: Array<{
    path: string;
    artifactId?: string;
    relevanceScore: number;
    contentMode: string;
    content: string;
    reason: string;
  }>;
  metadata: {
    totalTokens: number;
    budgetTokens: number;
    filesIncluded: number;
    filesExcluded: number;
    filesReferenced: number;
    artifactsCovered: string[];
    generatedAt: string;
  };
  taskId?: string;
};

/** Subset of CallGraph returned by buildCallGraph. */
export type CallGraph = Record<string, unknown>;

/** Subset of ContextMap returned by getContextMap. */
export type ContextMap = Record<string, unknown>;

export interface GetContextMapInput {
  instruction: string;
  candidates: Array<{
    path?: string;
    symbolId?: string;
    artifactId?: string;
  }>;
  depth?: number;
  direction?: "downstream" | "upstream" | "both";
  budgetTokens?: number;
}

// ---------------------------------------------------------------------------
// Bridge options and interface
// ---------------------------------------------------------------------------

export interface AnalyzerBridgeOptions {
  projectRoot: string;
  navigationIndex?: Record<string, unknown>;
  artifactPatterns?: Map<string, string[]>;
  manifests?: string[];
}

export interface AnalyzeImpactInput {
  direction: "downstream" | "upstream";
  maxDepth: number;
  limit: number;
}

export interface GenerateContextPackInput {
  budgetTokens: number;
  granularity: "file" | "symbol";
}

export interface BuildCallGraphInput {
  maxDepth: number;
  direction: "forward" | "reverse";
}

export interface AnalyzerBridge {
  buildGraph(): Promise<void>;
  getGraph(): Promise<DependencyGraph>;
  analyzeImpact(
    changed: string[],
    options?: Partial<AnalyzeImpactInput>,
  ): Promise<ImpactReport>;
  generateContextPack(
    changed: string[],
    options?: Partial<GenerateContextPackInput>,
  ): Promise<ContextPack>;
  buildCallGraph(
    entry: string,
    options?: Partial<BuildCallGraphInput>,
  ): Promise<CallGraph>;
  getContextMap(
    input: GetContextMapInput,
  ): Promise<ContextMap>;
  invalidateCache(): void;
  buildAnalyzerTools(): SdkFunctionTool[];
}

// ---------------------------------------------------------------------------
// Dynamic import helper
// ---------------------------------------------------------------------------

interface AnalyzerModule {
  buildAstGraph: (options: Record<string, unknown>) => Promise<{ graph: Record<string, unknown>; fileContents: Map<string, string> }>;
  buildDependencyGraph: (options: Record<string, unknown>) => Promise<DependencyGraph>;
  analyzeImpact: (options: Record<string, unknown>) => Promise<ImpactReport>;
  generateContextPack: (options: Record<string, unknown>) => Promise<ContextPack>;
  getContextMap: (options: Record<string, unknown>) => Promise<ContextMap>;
  buildCallGraph: (options: Record<string, unknown>) => CallGraph;
  createDefaultRegistry: () => Record<string, unknown>;
  loadExternalProviders?: (projectRoot: string, configPath?: string) => Promise<unknown[]>;
  integrateExternalInsights?: (
    graph: DependencyGraph,
    providers: unknown[],
    query: Record<string, unknown>,
  ) => Promise<{ graph: DependencyGraph }>;
}

const ANALYZER_PKG = "artifact-contracts-analyzer";

async function loadAnalyzerModule(): Promise<AnalyzerModule> {
  const mod = (await import(
    /* webpackIgnore: true */ ANALYZER_PKG
  )) as Record<string, unknown>;
  return mod as unknown as AnalyzerModule;
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createAnalyzerBridge(
  options: AnalyzerBridgeOptions,
): AnalyzerBridge {
  const { projectRoot, navigationIndex, artifactPatterns, manifests } = options;

  let cachedGraph: DependencyGraph | null = null;
  let cachedFileContents: Map<string, string> | null = null;
  let graphBuildPromise: Promise<void> | null = null;

  async function ensureGraph(): Promise<DependencyGraph> {
    if (cachedGraph) return cachedGraph;
    if (graphBuildPromise) {
      await graphBuildPromise;
      return cachedGraph!;
    }
    graphBuildPromise = doBuildGraph();
    await graphBuildPromise;
    return cachedGraph!;
  }

  async function doBuildGraph(): Promise<void> {
    let mod: AnalyzerModule;
    try {
      mod = await loadAnalyzerModule();
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      throw new Error(
        `artifact-contracts-analyzer is not available. ` +
          `Install it to use analyzer features: ${msg}`,
      );
    }

    const patterns = artifactPatterns ?? new Map<string, string[]>();
    const registry = mod.createDefaultRegistry();

    const astResult = await mod.buildAstGraph({
      projectRoot,
      artifactPatterns: patterns,
      registry,
    });

    cachedFileContents = astResult.fileContents;

    cachedGraph = await mod.buildDependencyGraph({
      ast: astResult.graph,
      projectRoot,
      artifactPatterns: patterns,
      manifests: manifests ?? [],
    });

    if (
      typeof (mod as { loadExternalProviders?: unknown }).loadExternalProviders ===
        "function" &&
      typeof (mod as { integrateExternalInsights?: unknown })
        .integrateExternalInsights === "function"
    ) {
      try {
        const loadProviders = (
          mod as {
            loadExternalProviders: (
              projectRoot: string,
            ) => Promise<unknown[]>;
          }
        ).loadExternalProviders;
        const integrate = (
          mod as {
            integrateExternalInsights: (
              graph: DependencyGraph,
              providers: unknown[],
              query: Record<string, unknown>,
            ) => Promise<{ graph: DependencyGraph }>;
          }
        ).integrateExternalInsights;
        const providers = await loadProviders(projectRoot);
        if (providers.length > 0) {
          const integration = await integrate(
            cachedGraph,
            providers,
            { projectRoot },
          );
          cachedGraph = integration.graph;
        }
      } catch {
        // External providers are optional — fail open.
      }
    }
  }

  const bridge: AnalyzerBridge = {
    async buildGraph(): Promise<void> {
      await ensureGraph();
    },

    async getGraph(): Promise<DependencyGraph> {
      return ensureGraph();
    },

    async analyzeImpact(
      changed: string[],
      opts?: Partial<AnalyzeImpactInput>,
    ): Promise<ImpactReport> {
      let mod: AnalyzerModule;
      try {
        mod = await loadAnalyzerModule();
      } catch {
        return {
          error:
            "artifact-contracts-analyzer is not installed. Install it as a peer dependency to use impact analysis.",
          changed,
        };
      }

      const graph = await ensureGraph();
      return mod.analyzeImpact({
        graph,
        changed,
        direction: opts?.direction ?? "downstream",
        ...(opts?.maxDepth !== undefined ? { maxDepth: opts.maxDepth } : {}),
        ...(opts?.limit !== undefined ? { limit: opts.limit } : {}),
        ...(navigationIndex ? { navigationIndex } : {}),
      });
    },

    async generateContextPack(
      changed: string[],
      opts?: Partial<GenerateContextPackInput>,
    ): Promise<ContextPack> {
      let mod: AnalyzerModule;
      try {
        mod = await loadAnalyzerModule();
      } catch {
        return {
          entries: [],
          metadata: {
            totalTokens: 0,
            budgetTokens: opts?.budgetTokens ?? 50_000,
            filesIncluded: 0,
            filesExcluded: 0,
            filesReferenced: 0,
            artifactsCovered: [],
            generatedAt: new Date().toISOString(),
          },
        } as ContextPack;
      }

      const graph = await ensureGraph();
      return mod.generateContextPack({
        graph,
        changed,
        projectRoot,
        ...(opts?.budgetTokens !== undefined
          ? { budgetTokens: opts.budgetTokens }
          : {}),
        ...(opts?.granularity ? { granularity: opts.granularity } : {}),
        ...(navigationIndex ? { navigationIndex } : {}),
        ...(cachedFileContents ? { fileContents: cachedFileContents } : {}),
      });
    },

    async buildCallGraph(
      entry: string,
      opts?: Partial<BuildCallGraphInput>,
    ): Promise<CallGraph> {
      let mod: AnalyzerModule;
      try {
        mod = await loadAnalyzerModule();
      } catch {
        return {
          error:
            "artifact-contracts-analyzer is not installed. Install it as a peer dependency to use call graph analysis.",
          entry,
        };
      }

      const graph = await ensureGraph();
      return mod.buildCallGraph({
        graph,
        entry,
        ...(opts?.maxDepth !== undefined ? { maxDepth: opts.maxDepth } : {}),
        ...(opts?.direction ? { direction: opts.direction } : {}),
      }) as CallGraph;
    },

    async getContextMap(input: GetContextMapInput): Promise<ContextMap> {
      let mod: AnalyzerModule;
      try {
        mod = await loadAnalyzerModule();
      } catch {
        return {
          error:
            "artifact-contracts-analyzer is not installed. Install it as a peer dependency to use context map analysis.",
          instruction: input.instruction,
        };
      }

      const graph = await ensureGraph();
      return mod.getContextMap({
        graph,
        projectRoot,
        instruction: input.instruction,
        candidates: input.candidates,
        ...(input.depth !== undefined ? { depth: input.depth } : {}),
        ...(input.direction ? { direction: input.direction } : {}),
        ...(input.budgetTokens !== undefined
          ? { budgetTokens: input.budgetTokens }
          : {}),
        ...(navigationIndex ? { navigationIndex } : {}),
        ...(cachedFileContents ? { fileContents: cachedFileContents } : {}),
      });
    },

    invalidateCache(): void {
      cachedGraph = null;
      cachedFileContents = null;
      graphBuildPromise = null;
    },

    buildAnalyzerTools(): SdkFunctionTool[] {
      return [
        {
          name: "analyzer.analyzeImpact",
          description:
            "Analyze the impact of changed files across the dependency graph",
          inputSchema: {
            type: "object",
            properties: {
              changed: {
                type: "array",
                items: { type: "string" },
                description: "File paths that changed",
              },
              direction: {
                type: "string",
                enum: ["downstream", "upstream"],
                description:
                  "Traversal direction. downstream = what breaks if this changes",
              },
              maxDepth: {
                type: "number",
                description: "Maximum BFS depth to explore",
              },
              limit: {
                type: "number",
                description:
                  "Cap the number of affected entries returned",
              },
            },
            required: ["changed"],
          },
          handler: async (
            args: Record<string, unknown>,
          ): Promise<string> => {
            const changed = args.changed as string[];
            const result = await bridge.analyzeImpact(changed, {
              direction: args.direction as
                | "downstream"
                | "upstream"
                | undefined,
              maxDepth: args.maxDepth as number | undefined,
              limit: args.limit as number | undefined,
            });
            return JSON.stringify(result);
          },
        },
        {
          name: "analyzer.generateContextPack",
          description:
            "Generate a token-budget-constrained context pack for changed files",
          inputSchema: {
            type: "object",
            properties: {
              changed: {
                type: "array",
                items: { type: "string" },
                description: "File paths that changed",
              },
              budgetTokens: {
                type: "number",
                description: "Token budget for the context pack",
              },
              granularity: {
                type: "string",
                enum: ["file", "symbol"],
                description: "Context granularity level",
              },
            },
            required: ["changed"],
          },
          handler: async (
            args: Record<string, unknown>,
          ): Promise<string> => {
            const changed = args.changed as string[];
            const result = await bridge.generateContextPack(changed, {
              budgetTokens: args.budgetTokens as number | undefined,
              granularity: args.granularity as
                | "file"
                | "symbol"
                | undefined,
            });
            return JSON.stringify(result);
          },
        },
        {
          name: "analyzer.getContextMap",
          description:
            "Build a structured context map from instruction and candidate paths/artifacts/symbols",
          inputSchema: {
            type: "object",
            properties: {
              instruction: {
                type: "string",
                description: "Natural-language task instruction",
              },
              candidates: {
                type: "array",
                items: {
                  type: "object",
                  properties: {
                    path: { type: "string" },
                    symbolId: { type: "string" },
                    artifactId: { type: "string" },
                  },
                },
                description:
                  "Candidate seeds from navigation-index (paths, symbols, artifacts)",
              },
              depth: {
                type: "number",
                description: "Traversal depth (default 2)",
              },
              direction: {
                type: "string",
                enum: ["downstream", "upstream", "both"],
                description: "Impact traversal direction",
              },
              budgetTokens: {
                type: "number",
                description: "Token budget for suggested read targets",
              },
            },
            required: ["instruction", "candidates"],
          },
          handler: async (
            args: Record<string, unknown>,
          ): Promise<string> => {
            const result = await bridge.getContextMap({
              instruction: args.instruction as string,
              candidates: args.candidates as GetContextMapInput["candidates"],
              depth: args.depth as number | undefined,
              direction: args.direction as GetContextMapInput["direction"],
              budgetTokens: args.budgetTokens as number | undefined,
            });
            return JSON.stringify(result);
          },
        },
        {
          name: "analyzer.buildCallGraph",
          description: "Build a call graph rooted at a symbol",
          inputSchema: {
            type: "object",
            properties: {
              entry: {
                type: "string",
                description:
                  "Entry symbolId (filePath#name) to root the call graph at",
              },
              maxDepth: {
                type: "number",
                description: "Maximum traversal depth",
              },
              direction: {
                type: "string",
                enum: ["forward", "reverse"],
                description:
                  "Traversal direction: forward (callees) or reverse (callers)",
              },
            },
            required: ["entry"],
          },
          handler: async (
            args: Record<string, unknown>,
          ): Promise<string> => {
            const entry = args.entry as string;
            const result = await bridge.buildCallGraph(entry, {
              maxDepth: args.maxDepth as number | undefined,
              direction: args.direction as
                | "forward"
                | "reverse"
                | undefined,
            });
            return JSON.stringify(result);
          },
        },
      ];
    },
  };

  return bridge;
}

// ---------------------------------------------------------------------------
// ContextPack prompt rendering utilities
// ---------------------------------------------------------------------------

/**
 * Duck-type check: does `value` look like a ContextPack?
 * Checks for the `entries` array and `metadata` object with expected fields.
 */
export function isContextPack(value: unknown): value is ContextPack {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  if (!Array.isArray(obj.entries)) return false;
  if (typeof obj.metadata !== "object" || obj.metadata === null) return false;
  const meta = obj.metadata as Record<string, unknown>;
  return (
    typeof meta.totalTokens === "number" &&
    typeof meta.budgetTokens === "number"
  );
}

/**
 * Render a ContextPack as a structured prompt section.
 * Used by task-runner when `variables.analyzerContextPack` is present.
 */
export function renderContextPackPrompt(pack: ContextPack): string {
  const m = pack.metadata;
  const sections: string[] = [];

  sections.push("## Source Code Context");

  sections.push(
    `### Budget\n- Tokens used: ${m.totalTokens} / ${m.budgetTokens}\n` +
      `- Files included: ${m.filesIncluded}, excluded: ${m.filesExcluded}, referenced: ${m.filesReferenced}`,
  );

  if (m.artifactsCovered.length > 0) {
    sections.push(
      `### Affected Artifacts\n${m.artifactsCovered.map((a) => `- ${a}`).join("\n")}`,
    );
  }

  if (pack.entries.length > 0) {
    const fileBlocks: string[] = [];
    for (const entry of pack.entries) {
      const ext = entry.path.split(".").pop() ?? "";
      const header = `#### ${entry.path} (relevance: ${entry.relevanceScore}, mode: ${entry.contentMode})`;
      const reason = `> ${entry.reason}`;
      if (entry.content) {
        fileBlocks.push(
          `${header}\n${reason}\n\n\`\`\`${ext}\n${entry.content}\n\`\`\``,
        );
      } else {
        fileBlocks.push(`${header}\n${reason}`);
      }
    }
    sections.push(`### Files\n${fileBlocks.join("\n\n")}`);
  }

  return sections.join("\n\n");
}
